export type UserRole = 'SYSTEM_ADMIN' | 'PARTNER_ADMIN' | 'PARTNER' | 'SUPERVISOR' | 'CANVASSER';

export interface User {
  id: number;
  role: UserRole;
  email: string;
  fullName?: string;
  partner?: any;
  enabled?: boolean;
  createdAt?: string;
  lastLoginLocation?: string;
  lastLoginAt?: string;
}

export const roleHierarchy: Record<UserRole, number> = {
  SYSTEM_ADMIN: 3,
  PARTNER_ADMIN: 2.5,
  PARTNER: 2,
  SUPERVISOR: 2,
  CANVASSER: 1,
};

export function getAllowedRoles(requiredRole: UserRole): UserRole[] {
  if (requiredRole === 'PARTNER') {
    return ['PARTNER', 'PARTNER_ADMIN'];
  }

  return [requiredRole];
}

export function hasRole(user: User | undefined, requiredRole: UserRole): boolean {
  if (!user) return false;
  return getAllowedRoles(requiredRole).includes(user.role);
}

export function hasMinimumRole(user: User | undefined, minimumRole: UserRole): boolean {
  if (!user) return false;
  return roleHierarchy[user.role] >= roleHierarchy[minimumRole];
}

export function canAccessRoute(user: User | undefined, route: string): boolean {
  if (!user) return false;
  
  const adminRoutes = ['/admin'];
  const partnerRoutes = ['/partner'];
  const supervisorRoutes = ['/supervisor'];
  const canvasserRoutes = ['/canvasser'];
  
  if (adminRoutes.some(routePrefix => route.startsWith(routePrefix))) {
    return user.role === 'SYSTEM_ADMIN';
  }
  
  if (partnerRoutes.some(routePrefix => route.startsWith(routePrefix))) {
    return hasRole(user, 'PARTNER');
  }

  if (supervisorRoutes.some(routePrefix => route.startsWith(routePrefix))) {
    return user.role === 'SUPERVISOR';
  }
  
  if (canvasserRoutes.some(routePrefix => route.startsWith(routePrefix))) {
    return user.role === 'CANVASSER';
  }
  
  return true; // Public routes
}

export function getRoleLoginPath(role: UserRole): string {
  const roleLoginPaths: Record<UserRole, string> = {
    SYSTEM_ADMIN: '/admin/login',
    PARTNER_ADMIN: '/partner/login',
    PARTNER: '/partner/login',
    SUPERVISOR: '/supervisor/login',
    CANVASSER: '/canvasser/login',
  };
  
  return roleLoginPaths[role];
}

export function getRoleBasePath(role: UserRole): string {
  const roleBasePaths: Record<UserRole, string> = {
    SYSTEM_ADMIN: '/admin',
    PARTNER_ADMIN: '/partner',
    PARTNER: '/partner',
    SUPERVISOR: '/supervisor',
    CANVASSER: '/canvasser',
  };

  return roleBasePaths[role];
}

export function getRoleDefaultPath(role: UserRole): string {
  if (role === 'CANVASSER') {
    return '/canvasser/check-registration';
  }

  if (role === 'SYSTEM_ADMIN') {
    return '/admin/dashboard';
  }

  return `${getRoleBasePath(role)}/dashboard`;
}
